import { faChevronDown, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { format, parse, isValid } from 'date-fns';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '../../core/components/Button';
import Calendar from '../../core/components/Calendar';
import DropdownMenu from '../../core/components/DropdownMenu';
import DateField, { defaultDateFormat } from '../../core/components/Fields/DateField';
import { FilterDropdownOption, TriggerButtonIndicator } from '../../core/components/Filter.tsx';
import { withIcon } from '../../core/components/Icon';
import classnames from '../../core/utils/classnames.tsx';
import { isoDateFormat } from '../../core/utils/dates.ts';

const TODAY = new Date();

// we started collecting data around 01.01.2023
export const MIN_DATE = new Date(2023, 0, 1);
// TODAY + 2 years
export const MAX_DATE = new Date(new Date().setFullYear(new Date().getFullYear() + 2));

const ClearIcon = withIcon(faXmark);
const ChevronDownIcon = withIcon(faChevronDown);

interface DateRange {
    from: Date;
    to?: Date;
}

interface DateRangeFilterDropdownProps {
    label?: string;
    options?: FilterDropdownOption[];
    range?: DateRange;
    onRangeChange?: (range: DateRange) => void;
    className?: string;
    dateFormat?: string;
    required?: boolean;
    minDate?: Date;
    maxDate?: Date;
}

export const getSelectedOption = (range: DateRange, options: FilterDropdownOption[]) => {
    // values come in like 'DDDD-MM-YYYY,DDDD-MM-YYYY' (start, end)
    const selectedValue =
        range?.to && range?.from ? `${format(range.from, isoDateFormat)},${format(range.to, isoDateFormat)}` : null;
    return options.find((option) => option.value === selectedValue);
};

export const formatDateRange = (range: DateRange, dateFormat: string = defaultDateFormat): string => {
    if (!range || (!range.from && !range.to)) {
        return '';
    }

    if (range.from && range.to) {
        if (format(range.from, dateFormat) === format(range.to, dateFormat)) {
            return format(range.from, dateFormat);
        }
        return `${format(range.from, dateFormat)} - ${format(range.to, dateFormat)}`;
    } else if (range.from) {
        return `Since ${format(range.from, dateFormat)}`;
    } else if (range.to) {
        return `Until ${format(range.to, dateFormat)}`;
    }
    return '';
};

const TriggerButton = ({
    label: defaultLabel,
    range,
    options,
    className,
    dateFormat = defaultDateFormat,
    onClear,
    required,
    ...props
}: any) => {
    const selectedOption = getSelectedOption(range, options);

    // in case no item is selected we show the default label
    let label: string = defaultLabel;
    if (selectedOption) {
        label = selectedOption.label;
    } else {
        label = formatDateRange(range, dateFormat);
    }

    if (label !== defaultLabel) {
        label = `${defaultLabel} ${label}`;
    }

    return (
        <Button
            className={classnames('flex items-center gap-2', className)}
            active={range?.from || range?.to}
            {...props}
        >
            <span>{label}</span>

            {!required && (range?.from || range?.to) ? (
                <TriggerButtonIndicator onClick={onClear} active>
                    <ClearIcon />
                </TriggerButtonIndicator>
            ) : (
                <TriggerButtonIndicator active={!!(range?.from || range?.to)} className="pointer-events-none">
                    <ChevronDownIcon />
                </TriggerButtonIndicator>
            )}
        </Button>
    );
};

export const DateRangeForm = ({
    range: initialRange,
    onSubmit,
    onCancel,
    dateFormat = defaultDateFormat,
    maxDate = MAX_DATE,
    minDate = MIN_DATE,
}) => {
    const { t } = useTranslation('analytics');

    const fromInputRef = useRef(null);
    const toInputRef = useRef(null);

    const [range, setRange] = useState<DateRange>(initialRange ?? { from: null, to: null });

    const handleSubmit = () => {
        onSubmit(range);
    };

    const handleSelect = (range: DateRange) => {
        setRange(range ?? { from: null, to: null });
    };

    useEffect(() => {
        setTimeout(() => {
            // skip the current event loop
            if (range?.from && !range?.to) {
                toInputRef.current?.focus();
            } else {
                fromInputRef.current?.focus();
            }
        }, 0);
    }, [range?.from, range?.to]);

    useEffect(() => {
        setRange(initialRange ?? { from: null, to: null });
    }, [initialRange?.from, initialRange?.to]);

    return (
        <div className="p-5 flex flex-col gap-4 w-full sm:w-[360px] border-t sm:border-t-0 sm:border-l border-line-weak border-solid">
            <div className="flex gap-4 flex-1">
                <div className="flex flex-col gap-1 flex-1 min-w-0">
                    <label className="text-sm text-content-medium">{t('dashboard.filters.dateRange.startDate')}</label>
                    <DateField
                        inputRef={fromInputRef}
                        disableDatePicker
                        placeholder={dateFormat}
                        value={range?.from ? format(range.from, isoDateFormat) : ''}
                        onValueChange={(value) =>
                            setRange({
                                ...range,
                                from: value ? parse(value, isoDateFormat, new Date()) : undefined,
                            })
                        }
                        format={dateFormat}
                        fromDate={minDate}
                        toDate={range?.to ?? maxDate}
                    />
                </div>

                <div className="flex flex-col gap-1 flex-1 min-w-0">
                    <label className="text-sm text-content-medium">{t('dashboard.filters.dateRange.endDate')}</label>
                    <DateField
                        inputRef={toInputRef}
                        disableDatePicker
                        placeholder={dateFormat}
                        value={range?.to ? format(range.to, isoDateFormat) : ''}
                        onValueChange={(value) =>
                            setRange({
                                ...range,
                                to: value ? parse(value, isoDateFormat, new Date()) : undefined,
                            })
                        }
                        format={dateFormat}
                        fromDate={range?.from ?? minDate}
                        toDate={maxDate}
                    />
                </div>
            </div>

            <div>
                <Calendar
                    mode="range"
                    selected={range ? range : undefined}
                    onSelect={handleSelect}
                    disabled={{
                        after: TODAY,
                        before: minDate,
                    }}
                    fromYear={minDate.getFullYear()}
                    toYear={maxDate.getFullYear()}
                    defaultMonth={range?.from ?? maxDate}
                />
            </div>

            <div className="flex justify-end gap-2">
                <button
                    className={classnames(
                        'flex gap-2 items-center bg-surface-weak text-content-strong shadow-sm rounded px-3 py-1.5 font-medium text-sm border border-solid border-line-strong outline-none',
                        'hover:bg-surface-strong transition-colors'
                    )}
                    onClick={onCancel}
                >
                    {t('dashboard.filters.dateRange.cancel')}
                </button>

                <button
                    className="flex items-center justify-center px-3 py-1.5 bg-surface-brand-strong-default rounded text-content-white dark:text-content-black font-medium text-sm hover:enabled:bg-surface-brand-strong-hover transition-colors duration-200 focus:enabled:bg-surface-brand-strong-focus active:enabled:bg-surface-brand-strong-active disabled:opacity-50"
                    onClick={handleSubmit}
                    // disabled={!allowEmpty && (!range?.from || !range?.to)}
                >
                    {t('dashboard.filters.dateRange.apply')}
                </button>
            </div>
        </div>
    );
};

const DateRangeFilterDropdown = ({
    label: defaultLabel,
    options,
    range: initialRange,
    onRangeChange,
    className,
    dateFormat = defaultDateFormat,
    required,
    minDate = undefined,
    maxDate = undefined,
}: DateRangeFilterDropdownProps) => {
    const { t } = useTranslation('analytics');

    const selectedOption = getSelectedOption(initialRange, options);

    const [isOpen, setIsOpen] = useState(false);
    const [isFormOpen, setIsFormOpen] = useState(!selectedOption);

    const handleSubmit = (range: DateRange) => {
        onRangeChange?.(range);
        setIsOpen(false);
    };

    const handleCancel = () => {
        setIsOpen(false);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onRangeChange?.({ from: null, to: null });
        setIsOpen(false);
    };

    const handleValueChange = (value: string) => {
        if (!value) return; // custom range selected

        const [startDate, endDate] = value.split(',');

        const newRange = {
            from: isValid(parse(startDate, isoDateFormat, new Date()))
                ? parse(startDate, isoDateFormat, new Date())
                : null,
            to:
                endDate && isValid(parse(endDate, isoDateFormat, new Date()))
                    ? parse(endDate, isoDateFormat, new Date())
                    : null,
        };
        onRangeChange(newRange);
        setIsOpen(false);
    };

    const handleSelectCustom = (e) => {
        e.preventDefault();
        setIsFormOpen(true);
    };

    useEffect(() => {
        setIsFormOpen(initialRange && (initialRange.from || initialRange.to) && !selectedOption);
    }, [selectedOption, isOpen]);
    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <div className="relative text-sm">
                <DropdownMenu.Trigger
                    asChild
                    onPointerDown={(e) => e.preventDefault()}
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <TriggerButton
                        label={defaultLabel}
                        range={initialRange}
                        options={options}
                        className={className}
                        dateFormat={dateFormat}
                        onClear={handleClear}
                        required={required}
                    />
                </DropdownMenu.Trigger>
            </div>
            <DropdownMenu.Portal>
                <DropdownMenu.Content align="start" className="p-0">
                    <div className="flex flex-col sm:flex-row">
                        <DropdownMenu.RadioGroup
                            value={!isFormOpen ? selectedOption?.value : null}
                            onValueChange={(value) => handleValueChange?.(value)}
                            className="p-1 w-full"
                        >
                            {options?.map((option) => (
                                <DropdownMenu.RadioItem key={option.value} value={option.value}>
                                    {option.label}
                                </DropdownMenu.RadioItem>
                            ))}
                            <DropdownMenu.RadioItem key="custom" value={null} onSelect={handleSelectCustom}>
                                {t('dashboard.filters.dateRange.custom')}
                            </DropdownMenu.RadioItem>
                        </DropdownMenu.RadioGroup>

                        {isFormOpen && (
                            <DateRangeForm
                                range={initialRange}
                                onSubmit={handleSubmit}
                                onCancel={handleCancel}
                                dateFormat={dateFormat}
                                minDate={minDate}
                                maxDate={maxDate}
                            />
                        )}
                    </div>
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu>
    );
};

export default DateRangeFilterDropdown;
